'use client'

import React from 'react'

interface YesNoSwitchProps {
    value: boolean | null
    onChange: (value: boolean) => void
    labels?: [string, string]
    error?: { message?: string }
    ariaLabelledBy?: string
    /** Préfixe des data-test, aligné par défaut sur FormSelect.vue (parité des sélecteurs Cypress). */
    testIdPrefix?: string
    className?: string
    /** Desktop track width, widened for long labels; mobile stays full width. */
    widthClassName?: string
    /** Clicking the active half flips the answer, so the whole track responds. Off for mode selectors. */
    invertOnActiveClick?: boolean
}

export default function YesNoSwitch({
    value,
    onChange,
    labels = ['Oui', 'Non'],
    error,
    ariaLabelledBy,
    testIdPrefix = 'form-select-option',
    className = '',
    widthClassName = 'md:w-[148px]',
    invertOnActiveClick = true,
}: YesNoSwitchProps) {
    const options: { label: string; answer: boolean }[] = [
        { label: labels[0], answer: true },
        { label: labels[1], answer: false },
    ]

    return (
        <div className={className}>
            {/* Les conteneurs teintés (famille crème, bloc conditionnel) posent --rm-switch-track à blanc. */}
            <div
                role="group"
                aria-labelledby={ariaLabelledBy}
                className={`flex h-[44px] w-full items-center gap-[2px] rounded-full bg-[var(--rm-switch-track,#F3F4F6)] p-[2px] md:h-[40px] ${widthClassName}`}
            >
                {options.map(({ label, answer }) => {
                    const isSelected = value === answer
                    // With no answer there is nothing to invert: the first click keeps the option that was pressed
                    const nextAnswer = invertOnActiveClick && value != null ? !value : answer
                    return (
                        <button
                            key={label}
                            type="button"
                            data-test={`${testIdPrefix}-${answer ? 1 : 0}`}
                            aria-pressed={isSelected}
                            onClick={() => onChange(nextAnswer)}
                            className={`flex-1 h-full min-w-0 rounded-full text-[14px] transition-colors duration-200 cursor-pointer noselect ${
                                isSelected ? 'bg-rm-blue font-semibold text-white' : 'bg-transparent text-rm-gray-dark'
                            }`}
                        >
                            {label}
                        </button>
                    )
                })}
            </div>
            {error?.message && <p className="mt-1 text-[12px] text-red-600">{error.message}</p>}
        </div>
    )
}
